import { Button } from '@/components/ui/button.js';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command.js';
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form.js';
import { LoaderIcon } from '@/components/ui/loader-icon.js';
import { useDebounce } from '@/components/ui/multi-select-utils.js';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover.js';
import { EmptyArray } from '@/lib/api-calls.js';
import { cn } from '@/lib/tailwind-utils.js';
import { Check, Search } from 'lucide-react';
import { ComponentProps, ReactNode, useEffect, useState } from 'react';
import { FieldPath, FieldPathValue, FieldValues, UseFormReturn } from 'react-hook-form';

type OptionsOrFetcher<T> = T | ((value: string) => T | Promise<T>);

interface FormComboBoxProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  form: UseFormReturn<TFieldValues>;
  name: TName;
  label: ReactNode;
  options: OptionsOrFetcher<Array<FieldPathValue<TFieldValues, TName>>>;
  placeholder?: string;
  renderOption: (option: FieldPathValue<TFieldValues, TName> | undefined) => ReactNode;
  renderOptionSelected?: (option: FieldPathValue<TFieldValues, TName> | undefined) => ReactNode;
  emptyOptionMessage?: string;
  optionHash: (option: FieldPathValue<TFieldValues, TName> | undefined) => string;
  optionToFilterableValues: (option: FieldPathValue<TFieldValues, TName>) => string[];
  triggerProps?: ComponentProps<typeof Button> & { 'data-testid'?: string };
  labelTestId?: string;
  required?: boolean;
  triggerSearchOnFocus?: boolean;
  className?: string;
  /**
   * If the options array is loading. Do not use this if options are fetched from a function.
   * When options is a fetcher function, the loading state is managed by this component internally.
   */
  isLoadingKnownOptions?: boolean;
  formDescription?: ReactNode;
  shouldHideCheck?: boolean;
  emptySearchOptionMessage?: string;
}

// Filter behavior inspired by https://github.com/shadcn-ui/ui/issues/458
export function FormComboBox<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  form,
  name,
  label,
  options,
  placeholder,
  emptyOptionMessage = 'No results',
  renderOption,
  renderOptionSelected = renderOption,
  optionHash,
  optionToFilterableValues: optionKeywordsForFilter,
  triggerProps = {},
  labelTestId,
  required = false,
  triggerSearchOnFocus = false,
  className = '',
  isLoadingKnownOptions = false,
  formDescription,
  shouldHideCheck = false,
  emptySearchOptionMessage = 'Start typing to see results',
}: FormComboBoxProps<TFieldValues, TName>) {
  const { className: triggerClassName, ...others } = triggerProps;
  const [open, setOpen] = useState(false);
  const [selectables, setSelectables] = useState<Array<FieldPathValue<TFieldValues, TName>>>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const debouncedSearchTerm = useDebounce(inputValue, 500);

  // options are fetched from search fn
  const onSearch = typeof options === 'function' ? options : undefined;
  // input search props for CommandInput if onSearch is provided
  // ensure CommandInput always gets a string value (fixes cmdk crash if value is undefined/null)
  const inputSearchProps = onSearch ? { onValueChange: setInputValue, value: String(inputValue ?? '') } : {};
  const emptyMessage = inputValue.trim() === '' ? emptySearchOptionMessage : emptyOptionMessage;

  useEffect(() => {
    if (Array.isArray(options)) setSelectables(options);
  }, [options]);

  useEffect(() => {
    const doSearch = async () => {
      setIsLoading(true);
      const res = await onSearch?.(debouncedSearchTerm);
      setSelectables(res || EmptyArray);
      setIsLoading(false);
    };

    const exec = async () => {
      if (!onSearch || !open) return;

      const query = (debouncedSearchTerm ?? '').trim();

      if (query === '') {
        if (triggerSearchOnFocus) {
          await doSearch();
        } else {
          setSelectables(EmptyArray);
          setIsLoading(false);
        }
        return;
      }

      await doSearch();
    };

    void exec();
  }, [debouncedSearchTerm, onSearch, triggerSearchOnFocus, open]);

  return (
    <>
      <FormField
        control={form.control}
        name={name}
        render={({ field }) => {
          const hasSelectedOption = !!field.value?.value;

          return (
            <FormItem className={cn('flex flex-col mt-1', className)}>
              <FormLabel className="font-bold mb-[6px]" data-testid={labelTestId}>
                {label}
                {required && '*'}
              </FormLabel>
              <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                  <FormControl className="h-10">
                    <Button
                      variant="outline"
                      role="combobox"
                      className={cn(
                        'w-2/3 justify-between',
                        !hasSelectedOption && 'text-muted-foreground',
                        triggerClassName,
                      )}
                      {...others}
                    >
                      <span className="truncate">
                        {hasSelectedOption ? renderOptionSelected(field.value) : placeholder || 'Select an option'}
                      </span>
                      <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </FormControl>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0">
                  <Command
                    shouldFilter={!onSearch}
                    filter={(_, search, keywords = []) => {
                      const searchableValues = keywords.join('--');
                      if (searchableValues.toLowerCase().includes(search.toLowerCase())) {
                        return 1;
                      }
                      return 0;
                    }}
                  >
                    <CommandInput placeholder={placeholder} className="h-9" {...inputSearchProps} />
                    <CommandList>
                      {isLoading || isLoadingKnownOptions ? (
                        <div className="flex justify-center items-center h-full p-3.5">
                          <LoaderIcon />
                        </div>
                      ) : (
                        <>
                          <CommandEmpty>{emptyMessage}</CommandEmpty>
                          <CommandGroup>
                            {(onSearch && field.value?.value
                              ? [field.value, ...selectables.filter((option) => option.value !== field.value.value)]
                              : selectables
                            ).map((option) => (
                              <CommandItem
                                key={optionHash(option)}
                                value={optionHash(option)}
                                keywords={optionKeywordsForFilter(option)}
                                onSelect={() => {
                                  form.setValue(name, option, { shouldValidate: true, shouldDirty: true });
                                }}
                              >
                                {renderOption(option)}
                                <Check
                                  className={cn(
                                    'ml-auto h-4 w-4',
                                    field.value
                                      ? optionHash(option) === optionHash(field.value)
                                        ? 'opacity-100'
                                        : 'opacity-0'
                                      : 'opacity-0',
                                    shouldHideCheck && 'hidden',
                                  )}
                                />
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </>
                      )}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {formDescription && <FormDescription>{formDescription}</FormDescription>}
              <FormMessage />
            </FormItem>
          );
        }}
      />
    </>
  );
}
